/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Index Status Component
 * 
 * Displays the current status of a Context Library index (playbooks, skills, MCP servers).
 * Shows build status, last build time, item count, and any errors.
 * 
 * @see product/docs/VELOCITY_PLAYBOOK_SYSTEM.md
 */

import { Badge } from '@/components/ui/badge';
import {
  CheckCircle,
  Loader2,
  Clock,
  AlertCircle,
} from 'lucide-react';
import type { IndexStatusResponseData } from '@shared/types/responses/vps-index';

export interface IndexStatusProps {
  /** Status data for the index */
  status: IndexStatusResponseData;
  /** Whether to show compact version */
  compact?: boolean;
}

/**
 * Format a relative time string from an ISO timestamp
 */
function formatRelativeTime(isoString?: string): string {
  if (!isoString) return 'Never';

  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHrs = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHrs / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString();
}

/**
 * Get the status configuration for display
 */
function getStatusConfig(status: IndexStatusResponseData['status']) {
  switch (status) {
    case 'completed':
      return {
        icon: CheckCircle,
        label: 'Indexed',
        className: 'bg-green-100 text-green-800 border-green-200',
        iconClassName: 'text-green-500',
      };
    case 'building':
      return {
        icon: Loader2,
        label: 'Building',
        className: 'bg-blue-100 text-blue-800 border-blue-200',
        iconClassName: 'text-blue-500 animate-spin',
      };
    case 'failed':
      return {
        icon: AlertCircle,
        label: 'Error',
        className: 'bg-red-100 text-red-800 border-red-200',
        iconClassName: 'text-red-500',
      };
    case 'idle':
    default:
      return {
        icon: Clock,
        label: 'Pending',
        className: 'bg-gray-100 text-gray-800 border-gray-200',
        iconClassName: 'text-gray-500',
      };
  }
}

/**
 * Index Status Component
 * 
 * Displays the build status of a Context Library index.
 */
export function IndexStatus({ status, compact = false }: IndexStatusProps) {
  const config = getStatusConfig(status.status);
  const Icon = config.icon;

  if (compact) {
    return (
      <Badge variant="outline" className={`text-xs ${config.className}`}>
        <Icon className={`h-3 w-3 mr-1 ${config.iconClassName}`} />
        {config.label}
        {status.itemCount !== undefined && status.status === 'completed' && (
          <span className="ml-1 opacity-70">({status.itemCount})</span>
        )}
      </Badge>
    );
  }

  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className={`h-4 w-4 ${config.iconClassName}`} />
      <span className="font-medium">{config.label}</span>
      {status.itemCount !== undefined && status.status === 'completed' && (
        <span className="text-muted-foreground">
          ({status.itemCount} items)
        </span>
      )}
      {status.lastBuilt && (
        <span className="text-muted-foreground">
          • {formatRelativeTime(status.lastBuilt)}
        </span>
      )}
      {status.status === 'failed' && status.error && (
        <span className="text-red-600 text-xs truncate max-w-[200px]" title={status.error}>
          {status.error}
        </span>
      )}
    </div>
  );
}

/**
 * Compact status badge for inline display
 */
export function IndexStatusBadge({ status }: { status: IndexStatusResponseData }) {
  return <IndexStatus status={status} compact />;
}
